import { Reference, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";

import AutomationCondition from "@components/automations/AutomationCondition";
import styles from "@components/automations/AutomationConditionGroup.module.css";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import AutomationConditionGroupForm from "@components/forms/AutomationConditionGroupForm";
import BodyCopy from "@components/typography/BodyCopy";
import InputLabel from "@components/typography/InputLabel";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import {
  AutomationConditionCreateDocument,
  AutomationConditionCreateMutation,
  AutomationConditionCreateMutationVariables,
  AutomationConditionFragmentDoc,
  AutomationConditionGroupDestroyDocument,
  AutomationConditionGroupDestroyMutation,
  AutomationConditionGroupDestroyMutationVariables,
  AutomationConditionKindEnum,
  AutomationFragment as AutomationType
} from "@typing/Generated";

type AutomationConditionBlockType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>;

type AutomationConditionGroupType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>["automationConditionGroups"][number];

type Props = {
  automationConditionBlock: AutomationConditionBlockType;
  automationConditionGroup: AutomationConditionGroupType;
  locked: boolean;
};

const AutomationConditionGroup = ({ automationConditionBlock, automationConditionGroup, locked }: Props) => {
  const deleteOpener = useOpener();

  const [destroyAutomationConditionGroup] = useMutationWithValidation<
    AutomationConditionGroupDestroyMutation,
    AutomationConditionGroupDestroyMutationVariables
  >(AutomationConditionGroupDestroyDocument, "automationConditionGroup", {
    update: (store, { data }) => {
      if (data?.automationConditionGroupDestroy?.id) {
        store.modify({
          fields: {
            automationConditionGroups(cachedRefs: readonly Reference[], { readField }) {
              return cachedRefs.filter(ref => data?.automationConditionGroupDestroy?.id !== readField("id", ref));
            }
          },
          id: store.identify({ ...automationConditionBlock })
        });
      }
    }
  });

  const [createAutomationCondition] = useMutation<
    AutomationConditionCreateMutation,
    AutomationConditionCreateMutationVariables
  >(AutomationConditionCreateDocument, {
    update: (store, { data }) => {
      if (data?.automationConditionCreate?.automationCondition) {
        const newCondition = data.automationConditionCreate.automationCondition;
        store.modify({
          fields: {
            automationConditions(cachedConditionRefs: readonly Reference[]) {
              const newConditionRef = store.writeFragment({
                data: newCondition,
                fragment: AutomationConditionFragmentDoc,
                fragmentName: "AutomationCondition"
              });
              if (newConditionRef) {
                return [...cachedConditionRefs, newConditionRef];
              }
              return [...cachedConditionRefs];
            }
          },
          id: store.identify({ ...automationConditionGroup })
        });
      }
    }
  });

  const handleDelete = useCallback(() => {
    destroyAutomationConditionGroup({ id: automationConditionGroup.id }).then(() => {
      deleteOpener.close();
    });
  }, [automationConditionGroup.id, deleteOpener, destroyAutomationConditionGroup]);

  const handleAddAutomationCondition = useCallback(() => {
    createAutomationCondition({
      variables: {
        automationConditionGroupId: automationConditionGroup.id,
        kind: AutomationConditionKindEnum.NEVER
      }
    });
  }, [automationConditionGroup.id, createAutomationCondition]);

  return (
    <>
      <div className={styles.conditionGroup}>
        <div>
          <AutomationConditionGroupForm automationConditionGroup={automationConditionGroup} locked={locked} />
        </div>
        <div className={styles.conditionsSection}>
          <InputLabel>
            <FormattedMessage id="pages.admin.automations.conditions" />
          </InputLabel>
          {automationConditionGroup.automationConditions.map((condition, index) => (
            <div key={condition.id}>
              <AutomationCondition
                automationCondition={condition}
                automationConditionGroup={automationConditionGroup}
                key={condition.id}
                locked={locked}
              />
              {index < automationConditionGroup.automationConditions.length - 1 && (
                <div className={styles.automationConditionOperator}>
                  <BodyCopy>
                    <FormattedMessage
                      id={`models.automationConditionGroup.operations.${automationConditionGroup.operation}`}
                    />
                  </BodyCopy>
                </div>
              )}
            </div>
          ))}
        </div>
        {!locked && (
          <div className={styles.buttons}>
            <TertiaryButton
              icon="add"
              onClick={handleAddAutomationCondition}
              textKey="pages.admin.automations.addCondition"
            />
            <TertiaryButton
              icon="trash"
              onClick={deleteOpener.open}
              textKey="pages.admin.automations.deleteConditionGroup"
            />
          </div>
        )}
      </div>
      <DeleteConfirmationAlert
        messageKey="pages.admin.automations.reallyDeleteAutomationConditionGroup"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default AutomationConditionGroup;
